// components/UploadFolderLoader.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, CircularProgress, IconButton } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import { CancelUploadModal, MuiBox, MuiTypography } from "components/atoms";
import ReUploadIcon from "assets/images/svg-icons/reUploadIcon.svg";
import { useTranslation } from "react-i18next";
import {
  clearFolderUpload,
  uploadFolderHandle,
  setFolderUploadStatus,
} from "store/slices/general";
import { abortFolderUpload } from "utils/uploadController";
import { UploadFolderAction } from "store/middlewares/general";
import {
  closeCenterModal,
  openCenterModal,
} from "store/slices/CenterModal.slice";

const UploadFolderLoader = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { uploadFolderState } = useSelector((state) => state.general);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { name, status, folderData } = uploadFolderState || {};

  const isUploading = status === "uploading";
  const isCanceledOrFailed = status === "canceled" || status === "failed";

  // Close cancel modal if upload finishes while it's open
  useEffect(() => {
    if (isCancelModalOpen && !isUploading) {
      dispatch(closeCenterModal());
      setIsCancelModalOpen(false);
    }
  }, [isCancelModalOpen, isUploading, dispatch]);

  const handleAbort = () => {
    abortFolderUpload();
    dispatch(setFolderUploadStatus("canceled"));
  };

  const openCancelModal = () => {
    setIsCancelModalOpen(true);
    dispatch(
      openCenterModal({
        content: (
          <CancelUploadModal
            title={t("uploadLoader.cancelAll")}
            description={t("uploadLoader.areYouSureCancelAllUploads")}
            cancelBtnName={t("uploadLoader.cancelUpload")}
            continueBtnName={t("uploadLoader.continueUploading")}
            onCancelUploads={() => {
              handleAbort();
              setIsCancelModalOpen(false);
            }}
            onContinue={() => setIsCancelModalOpen(false)}
          />
        ),
      }),
    );
  };

  const handleRetry = () => {
    if (!folderData) {
      console.warn("Cannot re-upload folder: folderData is missing");
      return;
    }

    dispatch(
      uploadFolderHandle({
        name: folderData.name || name,
        folderData,
      }),
    );

    dispatch(
      UploadFolderAction({
        zip: folderData.zip,
        parentId: folderData.parentId,
        name: folderData.name || name,
        onSuccess: () => {},
        onError: () => {},
      }),
    );
  };

  return (
    <MuiBox
      sx={{
        width: "100%",
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 24,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <MuiBox
        sx={{
          display: "flex",
          gap: "5px",
          width: "100%",
          p: "5px",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "background.blue400",
        }}
      >
        <MuiTypography
          variant="16"
          sx={{ fontWeight: 600, px: 1, textTransform: "lowerCase" }}
        >
          {isUploading
            ? `1 ${t("uploadLoader.folderSmall")} ${t("uploadLoader.uploadingLabel")}`
            : status === "uploaded"
              ? `1 ${t("uploadLoader.folderSmall")} ${t("uploadLoader.uploadedCap")}`
              : status === "canceled"
                ? `1 ${t("uploadLoader.folderSmall")} ${t("uploadLoader.cancelledCap")}`
                : `1 ${t("uploadLoader.folderSmall")} ${t("uploadLoader.failed")}`}
        </MuiTypography>

        <MuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            width: "fit-content",
          }}
        >
          {isUploading && (
            <Button
              size="small"
              onClick={openCancelModal}
              sx={{
                minWidth: "auto",
                px: "8px",
                fontSize: "14px",
                textTransform: "none",
              }}
            >
              {t("commonButton.cancel")}
            </Button>
          )}

          {!isUploading && (
            <IconButton
              size="small"
              onClick={() => dispatch(clearFolderUpload())}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </MuiBox>
      </MuiBox>

      {/* Body */}
      <MuiBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <MuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: 0,
            flex: 1,
          }}
        >
          {status === "uploaded" && (
            <CheckCircleIcon
              color="success"
              sx={{ fontSize: 20, flexShrink: 0 }}
            />
          )}
          {status === "failed" && (
            <DoNotDisturbOnIcon
              color="error"
              sx={{ fontSize: 20, flexShrink: 0 }}
            />
          )}
          {status === "canceled" && (
            <CancelIcon color="warning" sx={{ fontSize: 20, flexShrink: 0 }} />
          )}
          {isUploading && (
            <FolderIcon color="primary" sx={{ fontSize: 20, flexShrink: 0 }} />
          )}

          <MuiTypography
            variant="14"
            noWrap
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {isUploading && `${t("uploadLoader.uploadingLabel")}: `}
            {status === "uploaded" && `${t("uploadLoader.uploadedCap")}: `}
            {status === "failed" && `${t("uploadLoader.failed")}: `}
            {status === "canceled" && `${t("uploadLoader.cancelledCap")}: `}
            {name}
          </MuiTypography>
        </MuiBox>

        {isUploading && (
          <MuiBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "fit-content",
            }}
          >
            <CircularProgress
              enableTrackSlot
              variant="indeterminate"
              size={22}
              thickness={8}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
                borderRadius: "50%",
                boxShadow: "inset 0 0 0 3.5px #BBDEFB",
                flexShrink: 0,
              }}
            />
            {/* Quick cancel (no confirmation) – same as single-file X */}
            <IconButton size="small" onClick={handleAbort}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </MuiBox>
        )}

        {isCanceledOrFailed && (
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={handleRetry}
              disabled={!folderData}
            >
              <img
                src={ReUploadIcon}
                alt="retry"
                style={{ width: "22px", height: "22px" }}
              />
            </IconButton>
          </span>
        )}
      </MuiBox>
    </MuiBox>
  );
};

export default UploadFolderLoader;